import { useQuery } from "@tanstack/react-query";
import type { ContratSaas } from "@victor/types";
import { api } from "../../../lib/api";
import { PageSpinner } from "../../../ui/Spinner";
import {
  Badge,
  EmptyInline,
  Panel,
  SectionTitle,
  fmtDate,
  fmtMoney,
} from "./_ui";

const TYPE_LABEL: Record<string, string> = {
  souscription: "Souscription initiale",
  avenant_plan: "Avenant · Changement de plan",
  avenant_quotas: "Avenant · Évolution des quotas",
  resiliation: "Résiliation",
};

const TYPE_TONE: Record<string, Parameters<typeof Badge>[0]["tone"]> = {
  souscription: "emerald",
  avenant_plan: "accent",
  avenant_quotas: "accent",
  resiliation: "amber",
};

export function ContratsTab({ tenantId }: { tenantId: string }) {
  const q = useQuery({
    queryKey: ["tenant-contrats-saas", tenantId],
    queryFn: () => api.contratsSaas.list(tenantId),
  });

  async function openPdf(c: ContratSaas) {
    const token = window.__victorAdminToken ?? null;
    if (!token) {
      alert("Session expirée.");
      return;
    }
    const res = await fetch(api.contratsSaas.pdfUrl(tenantId, c.id), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      alert(`Échec téléchargement PDF (${res.status})`);
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
  }

  if (q.isLoading) return <PageSpinner />;

  return (
    <Panel padded={false}>
      <div className="px-5 pt-5">
        <SectionTitle
          icon={ICON.contract}
          desc="Contrats MyVictor signés par le tenant : souscription, avenants, résiliation."
        >
          Contrats
        </SectionTitle>
      </div>
      {!q.data || q.data.length === 0 ? (
        <div className="px-5 pb-5">
          <EmptyInline>Aucun contrat signé.</EmptyInline>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-y border-navy-100">
              <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                Numéro
              </th>
              <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                Type
              </th>
              <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                Signataire
              </th>
              <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                Plan
              </th>
              <th className="px-5 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-navy-500">
                Montant
              </th>
              <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                Signé le
              </th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-navy-100">
            {q.data.map((c) => {
              const isResil = c.type === "resiliation";
              const isAnnul = (c.resilieMotif ?? "").startsWith("ANNULÉ");
              return (
                <tr key={c.id} className="transition-colors hover:bg-navy-50/40">
                  <td className="px-5 py-3">
                    <p className="font-bold text-navy-800">{c.numero}</p>
                    <p className="font-mono text-[10px] text-navy-400 truncate">
                      {c.hasPdf ? "PDF disponible" : "PDF non généré"}
                    </p>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={TYPE_TONE[c.type] ?? "slate"}>
                        {TYPE_LABEL[c.type] ?? c.type}
                      </Badge>
                      {isResil && isAnnul ? (
                        <Badge tone="slate">Annulée</Badge>
                      ) : null}
                    </div>
                    {isResil && c.resilieEffectifAt && !isAnnul ? (
                      <p className="mt-1 text-[10px] text-navy-500">
                        Effet : {fmtDate(c.resilieEffectifAt)}
                      </p>
                    ) : null}
                  </td>
                  <td className="px-5 py-3 text-navy-700">
                    <p className="font-semibold">
                      {c.signataire.prenom} {c.signataire.nom}
                    </p>
                    <p className="text-xs text-navy-500">{c.signataire.email}</p>
                  </td>
                  <td className="px-5 py-3 text-xs font-bold uppercase text-navy-700">
                    {c.planCode}
                    <span className="ml-1 text-[10px] normal-case text-navy-400">
                      ({c.cycle})
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums font-semibold text-navy-800">
                    {fmtMoney(c.basePrixHt)} €
                  </td>
                  <td className="px-5 py-3 text-xs text-navy-600">
                    {fmtDate(c.signedAt)}
                    <p className="text-[10px] text-navy-400">IP {c.signedIp}</p>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => void openPdf(c)}
                      className="rounded-lg border border-navy-200 px-3 py-1 text-xs font-bold text-navy-700 hover:bg-navy-50"
                    >
                      PDF
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

const sp = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  viewBox: "0 0 24 24",
};
const ICON = {
  contract: (
    <svg {...sp}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <path d="M8 13h8M8 17h5" />
    </svg>
  ),
};
